import { IRowData } from "@multiweb/types/contracts";
import { DataInterfaceComparer } from "../../Global/Base/DataSources/DataInterfaceComparer";
import { IDataSource, IDataSourceLoadInfo, IOrderBy, OrderType } from "../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent } from "../../Global/Base/Events";
import { IWidgetResizeEvent, IWidgetSortableStartEvent, IWidgetSortableStopEvent } from "../UI.BaseControls/IEvent";
import { ElementUtils } from "../UI.ElementarControls/Helpers/ElementUtils";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { observeScroll } from "../utils";
import { DataGrid } from "./DataGrid";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { HeaderRow } from "./DataGridSubcontrols/Components/HeaderRow";
import { IListDataRowElement } from "./DataGridSubcontrols/Components/IDataRow";
import { ListDataRow } from "./DataGridSubcontrols/Components/ListDataRow";
import { HeaderTemplate } from "./DataGridSubcontrols/Templates/HeaderTemplate";
import { IListDataGrid } from "./IDataGrid";
import { IColumnOrderingChangedEventData, IColumnPositionChangedEventData } from "./IEvent";

export class ListDataGrid<R extends IListDataRowElement> extends DataGrid<R> implements IListDataGrid<R> {
    public readonly columnPositionChanged: GlobalEvent<IListDataGrid<R>, IColumnPositionChangedEventData> = new GlobalEvent<IListDataGrid<R>, IColumnPositionChangedEventData>();

    protected _headerDiv: HTMLElement;
    private _boundingRectChangeObserver: () => void;
    private _isScrolling: boolean = false;
    private _scrollingTimer: number | undefined = undefined;

    protected _showFrozen: boolean = true;

    private _frozenColumnCount: number = 0;

    public get frozenColumnCount(): number {
        return this._frozenColumnCount;
    }

    public set frozenColumnCount(v: number) {
        this._frozenColumnCount = v;
    }

    public override get dataSource(): IDataSource {
        return super.dataSource;
    }
    public override set dataSource(dataSource: IDataSource) {
        super.dataSource = dataSource;
        this.orderBy = ListDataGrid.getDefaultOrderByDefinition(this.dataSource);
    }

    public get showFrameMenu(): boolean {
        return this.headerRow.frameMenuVisibility;
    }

    public set showFrameMenu(v: boolean) {
        this.headerRow.frameMenuVisibility = v;
    }

    protected override _constructor(): void {
        super._constructor();

        this.orderBy = [];
        this.headerRow = new HeaderRow();

        this._dataRowFactory = () => new ListDataRow() as any as R;
    }

    protected override _attachDataGrid(): void {
        this._initColumnListeners();

        this.generateColumnsOrder();

        super._attachDataGrid();
    }

    /**
     * Inicializuje listenery pro sloupce
     * @param columns Přidávané sloupce (default = všechny)
     */
    private _initColumnListeners(columns: Column[] = this.columns): void {
        columns.forEach((column) => {
            column.registerEventListener("resizabledrag", (e: IWidgetResizeEvent) => {
                const targetColumn = (e.target as Column);
                targetColumn.width = e.detail.width;

                this.dataRows.forEach((row) => ListDataGrid._resizeCell(row, targetColumn, e.detail.width));

                this.headerRow.recountWidth(this.columns);

                this._updateResizeDivSize();

                this.resizeGrid();
            });
        });
    }

    /**
     * Spustí odpovídající resize metodu podle typu sloupce
     * @param row Řádek
     * @param column Sloupec
     * @param width Nová šířka
     */
    private static _resizeCell(row: IListDataRowElement, column: Column, width: number): void {
        if (column.withoutAttribute) {
            row.resizeCellByIndex(column.order, width);
        } else {
            row.resizeCellByFullAttrName(column.fullAttrName, width);
        }
    }

    /**
     * Je zavoláno vždy po zobrazení DG pokud byl skrytý ať už styly nebo právama
     * @protected
     */
    protected override _recalculateAfterVisible(): void {
        this.resizeHeaderFirstColumn();
        if (this._headerDiv && this._listDiv) {
            if (this._listDiv.scrollLeft != this._headerDiv.scrollLeft) {
                this._headerDiv.scrollLeft = this._listDiv.scrollLeft;
                let i = 1;
                /*
                 * Je to workaround chování prohližečů. Protože _listDiv, obsahuje svuj vnitrni scrollbar a hlavička ne, tak pokud
                 * dojde k tomu, že jse doscrolovano na konec a přepne se na jiný frame, dojde k překresleni a při návratu
                 * kdy se prohlížeč pokouší zobrazit data a scroll na původním místě dojde k desynchronizaci, ktera by se
                 * sice spravila zascrollovanim, ale pokud zustaneme jen u zobrazeni byly by sloupce data a sloupce hlavicky
                 * desynchronizovany. A jelikoz prohlizec neposila o tomto zadnou udalost musi se to resit takto.
                 */
                const int = setInterval(() => {
                    if (this._listDiv.scrollLeft != this._headerDiv.scrollLeft) {
                        this._headerDiv.scrollLeft = this._listDiv.scrollLeft;
                        if (i++ == 30) {
                            clearInterval(int);
                        }
                    } else {
                        clearInterval(int);
                    }
                }, 30);
            }
        }
    }

    protected override _renderHeader(): void {
        this.classList.add(...this.headerRow.extraClasses);

        const pomDiv: HTMLDivElement = createElement({ css: { width: "100%" } }) as HTMLDivElement;

        this._headerDiv = createElement({ classes: "header-div" });

        const headerTable = createElement({ type: "table" }) as HTMLTableElement;
        const thead = createElement({ type: "thead", classes: "table-head" });
        headerTable.appendChild(thead);

        this._headerDiv.appendChild(pomDiv);

        pomDiv.appendChild(headerTable);
        this._mainListDiv.appendChild(this._headerDiv);

        /**
         * Někdy dochází k rozhození hlavičky a posunu sloupcu aniž by to bylo chtěné.
         * Tento observer hlídá změny posunu hlavičky a při změně posune i sloupce.
         */
        this._boundingRectChangeObserver = observeScroll(pomDiv, () => {
            if (!this._isScrolling) {
                this._repositionHeader();
            }
        });

        thead.appendChild(this.headerRow);

        this.headerRow.render(this, this.columns, this.orderBy);

        // Vrací pozici hlavičky sloupce, bere v úvahu i neviditelné a počítá se od 1, aby odpovídala orderu v profilech
        function getElementPosition(element: Element) {
            let i = 1;
            while ((element = (element.previousSibling as Element))) {
                if ((element.classList.contains("HeaderCell") || element.classList.contains("fake-header-cell")) && !element.classList.contains("header-framemenu-button"))
                    i++;
            }
            return i;
        }

        // zpracování třídící události
        this.headerRow.registerEventListener("sortablestart", (e: IWidgetSortableStartEvent) => {
            (e.detail.placeholder as HTMLElement).style.width = (e.detail.element as HTMLElement).style.width;
            e.detail.element.setAttribute("data-oldposition", getElementPosition(e.detail.element).toString()); // uložíme si do elementu původní pozici
            this._headerDiv.scrollLeft = this._listDiv.scrollLeft;
        });

        this.headerRow.registerEventListener("sortablestop", (e: IWidgetSortableStopEvent) => {
            const element = e.detail.element as HeaderTemplate;
            const oldPosition = Number(e.detail.element.getAttribute("data-oldposition"));
            const newPosition = getElementPosition(e.detail.element);

            if (oldPosition === newPosition) {
                return;
            }
            const eventData: IColumnPositionChangedEventData = {
                oldColumnsVisibility: this.columns.map(column => column.visible),
                column: element.parentColumn,
                newPosition,
                oldPosition,
            };

            this.setColumnOrder(element.parentColumn, newPosition);
            this.columnPositionChanged.trigger(this, eventData);
            this.callEvent("ColumnPositionChanged", eventData);
        });
    }

    public setColumnsOrder(orderList: Array<{ column: Column; profile: { fullAttrName: string; order?: number; visible?: boolean; frozen?: boolean } }>): void {
        const clm: Array<Column> = [];

        //const htemplates = Array.from(this.querySelectorAll("tsc-headerrow > .HeaderCell")) as HeaderTemplate[];
        const htemplates = Array.from(this.querySelectorAll("tsc-headerrow > .HeaderCell")) as HeaderTemplate[];
        if (htemplates.length === 0)
            return;

        const parentElement = htemplates[0].parentElement;

        const orderedHtemps: { [attrName: string]: HeaderTemplate } = {};
        const lastItem = this.querySelector("tsc-headerrow > .no-sort-item:last-child");

        for (const item of orderList) {
            if (item.profile.order == undefined || item.profile.visible != undefined && item.profile.visible === false || item.profile.frozen) {
                continue;
            }

            const htemp = htemplates.find((element) => element.parentColumn === item.column);

            orderedHtemps[item.profile.fullAttrName] = htemp;
            const oldIndex = htemplates.filter((item) => !item.classList.contains("no-sort-item")).indexOf(htemp) + 1;
            const index = htemplates.indexOf(htemp);
            htemplates.splice(index, 1);

            for (const row of this.dataRows) {
                row.changeCellPositionByFullAttrName(item.profile.fullAttrName, (oldIndex < item.profile.order) ? item.profile.order : item.profile.order - 1);
            }
        }

        let position = 1;
        let visiblePosition = 0;
        let item: { column: Column; profile: { fullAttrName: string; order?: number; visible?: boolean; frozen?: boolean } } = undefined;
        while (orderList.length > 0 || htemplates.length > 0 || item != undefined) {
            if (htemplates.length > 0 && !htemplates[0].classList.contains("no-sort-item"))
                visiblePosition++;
            if (item == undefined && orderList.length > 0)
                item = orderList.shift();

            if (htemplates.length > 0 && htemplates[0].classList.contains("invisible")) {
                const htemp = htemplates[0];
                clm.push(htemp.parentColumn);
                htemplates.splice(0, 1);
                parentElement.appendChild(htemp);
                continue;
            }

            if (item != undefined && (item.profile.order == undefined || item.profile.visible != undefined && item.profile.visible === false || item.profile.frozen)) {
                item = undefined;
                continue;
            }

            if (item != undefined && visiblePosition === item.profile.order || htemplates.length === 0) {
                const htemp = orderedHtemps[item.profile.fullAttrName];
                clm.push(htemp.parentColumn);
                item = undefined;
                htemp.parentColumn.order = position;
                parentElement.appendChild(htemp);
            } else {
                const htemp = htemplates[0];
                clm.push(htemp.parentColumn);
                htemplates.splice(0, 1);
                htemp.parentColumn.order = position;
                parentElement.appendChild(htemp);
            }

            position++;
        }
        parentElement.appendChild(lastItem);

        this.columns = clm;
    }

    /**
     * Vykresli pozadovanou "stranku" zaznamu
     */
    protected override _renderData(eventData?: IDataSourceLoadInfo): void {
        this.headerRow.scrollBarBufferVisible = eventData !== undefined;
        if (!this._dataTable)
            return;

        super._renderData(eventData);

        this.resizeHeaderFirstColumn();
    }

    protected override _createRows(data: Iterable<IRowData>): void {
        if (this.frozenColumnCount > 0) {
            this.headerRow.render(this, this.columns, this.orderBy, null, false);
        }
        super._createRows(data);
    }

    protected override _renderRows(fromRowIndex: number, toRowIndex: number): void {
        for (let i = fromRowIndex; i < toRowIndex; i++) {
            const dataRow = this.dataRows[i];
            this._dataTable.appendChild(dataRow.render());

            if (i === 0) { // prvni vlozeny radek
                dataRow.addEventListener("controlElementWidthChanged", () => {
                    this.resizeHeaderFirstColumn();
                });
            }
        }
    }

    /**
     * Zakáže možnost řazení kliknutím na hlavičku sloupce
     */
    public disableSort(): void {
        this.headerRow.disableSort = true;
    }

    public override scrollGrid(ev: UIEvent): void {
        this._repositionHeader();

        if (this._scrollingTimer) {
            clearTimeout(this._scrollingTimer);
        }
        this._scrollingTimer = window.setTimeout(() => {
            this._isScrolling = false;
            this._scrollingTimer = undefined;
        }, 200);
        this._isScrolling = true;

        super.scrollGrid(ev);
    }

    protected _repositionHeader(): void {
        if (this.renderHeader && this._renderHeader && this._headerDiv) {
            const scrollN = this._listDiv ? this._listDiv.scrollLeft : 0;
            this._headerDiv.scrollLeft = scrollN;
            this.classList.toggle("shadow", scrollN > 0);
        }
    }

    public computeChoiceCellWidth(includeMargin: boolean = true): number {
        let dataRow: R;
        let computedFromDeleted = false;

        const getRowFromCollection = (collection: R[]) => {
            return (collection && collection.length > 0) ? collection[0] : null;
        };

        dataRow = getRowFromCollection(this.dataRows.filter(row => !row.isDeleted));

        if (!dataRow) {
            dataRow = getRowFromCollection(this.dataRows);
            computedFromDeleted = true;
        }

        const cellElement = dataRow?.controlsCellElement;
        if (!cellElement) {
            return 0;
        }
        if (dataRow && computedFromDeleted) {
            dataRow.isDeleted = false;
        }
        const computedStyle = window.getComputedStyle(cellElement);

        const marginWidth = (+computedStyle.marginLeft.replace("px", "")) + (+computedStyle.marginRight.replace("px", ""));
        const choiceCellWidth = cellElement.offsetWidth + (includeMargin ? marginWidth : 0);

        if (dataRow && computedFromDeleted) {
            dataRow.isDeleted = true;
        }
        return choiceCellWidth;
    }

    public resizeHeaderFirstColumn(): void {
        if (this.dataRows.length > 0) {
            const width = this.computeChoiceCellWidth(false);
            this.headerRow.setChoiceElementWidth(width);
        }
    }

    public setOrderBy(ordering: IOrderBy[], callEvent: boolean = true): boolean {
        if (DataInterfaceComparer.arraysEquals(ordering, this.orderBy)) // netřídit, pokud je nové třídění stejné jako předchozí
            return false;

        for (const column of this.columns) { // prochází všechny sloupce
            let orderType: OrderType = null;

            for (const ord of ordering) {
                if (ord.attributeName === column.orderAttrName) { // pokud se sloupec vyskytuje v poli atributů k setřízení
                    orderType = ord.order;

                    break;
                }
            }

            column.headerCell.updateOrderType(orderType);
        }

        this.orderBy = ordering;
        this._bindingData = true;
        this.dataSource.orderBy = this.orderBy;
        this._refreshSelection = false;
        if (callEvent) {
            this.callEvent("OrderingChanged", { order: this.orderBy } as IColumnOrderingChangedEventData);
        }

        return true;
    }

    /**
     * Vrací defaultní definici OrderBy.
     * Pokud je k dispozici dataSource, bere se jako atribut jeho dataKey, jinak "ID".
     */
    public static getDefaultOrderByDefinition(dataSource?: IDataSource): IOrderBy[] {
        return [
            {
                attributeName: (dataSource && dataSource.dataKey) || "ID",
                order: OrderType.ascending,
            },
        ];
    }

    /**
     * Nastaví defaultní třídění
     * Pokud je nastaveno, tak podle definice formuláře, jinak podle dataKey (nebo ID)
     * @param formDefinitionOrderBy Třídění podle definice formuláře
     * @returns Vrací true, pokud bylo třídění změněno. Pokud bylo nastavované stejné jako předchozí, vrací false.
     */
    public setDefaultOrderBy(formDefinitionOrderBy: IOrderBy[]): boolean {
        let ob: IOrderBy[];
        if (formDefinitionOrderBy && formDefinitionOrderBy.length > 0)
            ob = [...formDefinitionOrderBy];
        else
            ob = ListDataGrid.getDefaultOrderByDefinition(this.dataSource);

        return this.setOrderBy(ob, false);
    }

    /**
     * Přesune sloupec na novou pozici
     * @param column přesouvaný sloupec
     * @param newPosition nová pozice přesouvaného sloupce (počítá se vždy od 1)
     * @param forced příznak, že přesunutí sloupců gridu je vynuceno přesunutím položek na panelu 'Nastavení sloupců'
     */
    public setColumnOrder(column: Column, newPosition: number, forced: boolean = false): void {
        const clms: Array<Column> = [ ...this.columns.filter((clm) => !clm.isFrozen) ]; // všechny sloupce gridu vloží do pomocného pole, které se dále setřídí
        const frozenClms = [ ...this.columns.filter((clm) => clm.isFrozen) ];

        const oldPosition = column.order; // pořadí uložené v profilu, které se počítá od 1
        const actualPosition = clms.findIndex(clm => clm.attrName === column.attrName); // pozice sloupce před přesunutím

        // Při vynuceném přesunutí je potřeba přepočítat 'newPosition', protože do ní nejsou zahrnuté neviditelné sloupce na panelu 'Nastavení sloupců'
        if (forced) {
            // mezi viditelnými sloupci na seznamu (protože 'newPosition' bere v úvahu jen viditelné sloupce) se najde sloupec, na jehož pozici se má 'column' přesunout
            const columnAtNewPosition = this.columns.filter(clm => clm.visible === true)[newPosition - 1];
            newPosition = this.columns.indexOf(columnAtNewPosition) + 1;
        }

        let spliceClmns: boolean = false;
        for (const [index, clm] of clms.entries()) {
            // Pokud je už sloupec na nové pozici, není potřeba ho přesouvat
            if (actualPosition + 1 === newPosition) {
                clm.order = index + 1; // sloupec je na své pozici, nastaví se mu podle ní order + 1 (protože v profilech se počítá od 1)
            } else {
                spliceClmns = true; // Pokud není na nové pozici je třeba popřesouvat sloupce v poli na správné pozice
                break;
            }
        }

        // Pokud je potřeba sloupce v poli přesouvat, vyjme se prvek na aktuální pozici a vloží se na pozici novou
        if (spliceClmns) {
            const cutOutClmPosition = clms.length === newPosition ? newPosition : newPosition - 1; // pokud se vkládá na poslední pozici musí se přičíst 1, protože splice neodsune poslední prvek před vkládaný
            const cutOutClm = clms.splice(actualPosition, 1)[0]; // vyjme z pole přesouvaný sloupec na jeho aktuální pozici
            clms.splice(cutOutClmPosition, 0, cutOutClm); // vloží sloupec na novou pozici
            clms.forEach((column, index) => column.order = index + 1); // pořadí sloupců v poli už je správně, nastaví se jim order, podle indexu + 1 (protože order uloženýho v profilech se počítá od 1)
        }

        this.columns = [...frozenClms, ...clms]; // setřízené pole se uloží jak nový seznam sloupců gridu

        this.renderColumns(this.columns, this.orderBy, 0, false);

        // Přesouvání buněk u jednotlivých řádků gridu
        for (const row of this.dataRows) {
            if (column.withoutAttribute) {
                row.changeCellPositionByIndex(oldPosition, newPosition);
            } else {
                row.changeCellPositionByFullAttrName(column.fullAttrName, newPosition);
            }
        }
    }

    /**
    * Vygeneruje pro sloupce jejich pořadí v seznamu
    */
    public generateColumnsOrder(): void {
        let order = 1; // Kvůli kompatibilitě s profily, které se počítají také od 1
        for (const column of this.columns) {
            column.order = order++;
        }
    }

    public setColumnWidth(column: Column, width: number): void {
        if (column.width === width || !column.visible)
            return;

        const element = column.headerTemplate;

        element.style.width = width + "px";
        column.width = width;

        for (const row of this.dataRows) {
            row.resizeCellByFullAttrName(element.parentColumn.fullAttrName, width);
        }
    }

    public override setFilters(filters: any[]): void {
        super.setFilters(filters);

        this._headerDiv.classList.toggle("show-filter", filters && filters.length > 0);
    }

    /**
     * Odstraní z dataGridu všechny dynamické slopuce
     */
    public removeDynamicColumns(): void {
        const dynamicColumns = this.columns.filter(column => column.isDynamic);

        dynamicColumns.forEach((column) => {
            this._removeColumn(column);
        });
    }

    /**
     * Odstraní z dataGridu jeden konkrétní sloupec
     * @param column - sloupec, který má být odebrán
     */
    protected _removeColumn(column: Column): void {
        const columnIndex = this.columns.indexOf(column);

        if (columnIndex > -1) {
            this.columns.splice(columnIndex, 1);
            this.dataRows.forEach(row => row.removeCellByIndex(columnIndex));
            column.headerTemplate?.remove();
            column.remove();
        }
    }

    /**
     * Vykresleni kontejneru pro data
     */
    protected override _renderDataContainer(): void {
        super._renderDataContainer();

        this._eventsController.addListener(this._listDiv, "scroll", (ev: UIEvent) => {
            this._scroll(ev, "normal");
        }, { options: { passive: true } });
    }

    /**
     * Pohyb v seznamu na vybrany radek
     */
    public scrollToSelectedRow(): void {
        const index: number = this.dataRows.indexOf(this.lastClickedRow);
        if (index === -1) {
            this._listDiv.scrollTop = 0;
            return;
        }
        // kontrola, aby doslo ke scrollovani pouze pokud neni element viditelny
        const visibleRange = {
            start: this.offset().top + this.scrollTop,
            end: this.offsetHeight + this.scrollTop,
        };
        const rowTop = ElementUtils.offset(this.lastClickedRow).top;
        const rowBottom = rowTop + this.lastClickedRow.offsetHeight;

        if (rowTop > visibleRange.start && rowBottom < visibleRange.end)
            return;

        if (this.selectedRows.length > 0 && index >= 0)
            this._listDiv.scrollTop = (this.selectedRows[0].clientHeight + 1) * index;
        else
            this._listDiv.scrollTop = 0;
    }

    /**
     * Vykreslí do seznamu sloupce ze vstupu a zaregistruje u nich potřebné listenery
     * @param columns - sloupce k vykreslení
     * @param _orderBy
     * @param startIndex - od tohoto indexu mají být nové sloupce číslovány
     * @param addNoSortItem - příznak určující, zda se má dorovnávat šířka scrollbaru
     * @param addCells - příznak určující, zda se mají na konec každého řádku přidat buňky nových sloupců
     */
    public renderColumns(columns: Array<Column>, _orderBy: IOrderBy[], startIndex: number = 0, addNoSortItem: boolean = true, addCells: boolean = false): void {
        if (addCells) {
            this.dataRows.forEach(row => {
                row.addCells(columns);
                row.render();
            });
        }
        this._initColumnListeners(columns);

        this.headerRow.render(this, columns, this.orderBy, startIndex, addNoSortItem);
    }

    public override dispose(): void {
        this.clearDataRows();
        this.columnPositionChanged.removeAll();
        this.orderBy = [];
        this._boundingRectChangeObserver?.();

        super.dispose();
    }
}

registerCustomElement("tsc-list-data-grid", ListDataGrid);
